import React, { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LazyImg } from "@/components/LazyImg";
import {
  ArrowRight,
  CheckCircle2,
  Phone,
  MapPin,
  Layers,
  Images,
} from "lucide-react";
import { trackCall, trackQuote } from "@/lib/track";
import { ProjectLightbox } from "@/components/ProjectLightbox";

export interface ProjectImage {
  id: number;
  title: string;
  location: string;
  area: string;
  system: string;
  description: string;
  url: string;
  category: string;
  gallery?: string[];
}

import { PROJECT_RESTORED_IMAGES } from "@/data/restoredAssets";
import { EXETER_NEWBUILD } from "@/data/cdn-exeter-newbuild";
import { EXMOUTH_COASTAL } from "@/data/cdn-exmouth-coastal";
import { DEERPARK_POOL_1 } from "@/data/cdn-deerpark-pool-1";
import { DEERPARK_POOL_2 } from "@/data/cdn-deerpark-pool-2";
import { BLACKBROOK_TAUNTON } from "@/data/cdn-blackbrook-taunton";
import { UFH_SCREED_1 } from "@/data/cdn-ufh-screed-1";
import { UFH_SCREED_2 } from "@/data/cdn-ufh-screed-2";
import {
  FLOW_SCREED_1,
  FLOW_SCREED_2,
  FLOW_SCREED_3,
} from "@/data/cdn-flow-screed";
import { BEFORE_AFTER_1, BEFORE_AFTER_2 } from "@/data/cdn-before-after";
import { BEFORE_AFTER_3, BEFORE_AFTER_4 } from "@/data/cdn-before-after-2";
import { PROJECT_A_IMG } from "@/data/cdn-project-a";
import { PROJECT_B_IMG } from "@/data/cdn-project-b";

export const PROJECT_IMAGES: ProjectImage[] = [
  {
    id: 1,
    title: "Open-plan ground floor poured for a new-build in Exeter",
    location: "Exeter, Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Cemfloor liquid screed pumped over underfloor heating for a seamless, laser-flat open-plan living area.",
    url: EXETER_NEWBUILD,
    category: "Self-Build",
  },
  {
    id: 2,
    title: "UFH screed laid over a coastal apartment in Exmouth",
    location: "Exmouth, Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Ultra-thin 35mm liquid screed coverage over water underfloor heating pipes for maximum thermal efficiency.",
    url: EXMOUTH_COASTAL,
    category: "Residential",
  },
  {
    id: 3,
    title: "Swimming pool base screed pour in Honiton",
    location: "Honiton, Devon",
    area: "Devon",
    system: "Cemfloor CT-C30",
    description:
      "High-strength C30 screed installed as a swimming pool base at a Grade II listed estate near Honiton built in the early 1700s. Precision pour completed to preserve the historic fabric of this significant property.",
    url: DEERPARK_POOL_1,
    gallery: [DEERPARK_POOL_1, DEERPARK_POOL_2],
    category: "Commercial",
  },
  {
    id: 4,
    title: "Renovation screed for a barn conversion near Honiton",
    location: "Honiton, Devon",
    area: "Devon",
    system: "Cemfloor Non-Sanding",
    description:
      "Precision-levelled flow screed poured over uneven historic stone barn subfloors.",
    url: PROJECT_RESTORED_IMAGES[2],
    category: "Renovation",
  },
  {
    id: 5,
    title: "Residential screed pour at Blackbrook, Taunton",
    location: "Taunton, Somerset",
    area: "Somerset",
    system: "Cemfloor Therm",
    description:
      "Liquid screed installed over underfloor heating for a new-build home near Taunton.",
    url: BLACKBROOK_TAUNTON,
    category: "Residential",
  },
  {
    id: 6,
    title: "Self-build UFH pour near Tiverton",
    location: "Tiverton, Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Clean, crack-free screed surface for a custom self-build home in mid Devon.",
    url: PROJECT_RESTORED_IMAGES[3],
    category: "Self-Build",
  },
  {
    id: 7,
    title: "Estuary-side residential retrofit in Topsham",
    location: "Topsham, Devon",
    area: "Devon",
    system: "Fast-Drying Cemfloor",
    description:
      "Rapid-setting liquid screed allowing quick floor drying and fast commissioning for residential retrofit.",
    url: PROJECT_RESTORED_IMAGES[4],
    category: "Residential",
  },
  {
    id: 8,
    title: "Garage floor refinished in Torquay",
    location: "Torquay, Devon",
    area: "Devon",
    system: "Cemfloor CT-C30",
    description:
      "Heavy-duty C30 screed transforming a worn domestic garage into a level, durable surface.",
    url: PROJECT_RESTORED_IMAGES[5],
    category: "Garage",
  },
  {
    id: 9,
    title: "Fresh liquid screed pour in an Exeter extension",
    location: "Exeter, Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Laser-levelled flow screed for a modern kitchen/dining room extension in central Exeter.",
    url: PROJECT_RESTORED_IMAGES[0],
    category: "Residential",
  },
  {
    id: 10,
    title: "Smooth finish on a garden room pour in Exmouth",
    location: "Exmouth, Devon",
    area: "Devon",
    system: "Cemfloor Non-Sanding",
    description:
      "Laitance-free finish ready for direct tiling without sanding in a garden office studio.",
    url: PROJECT_RESTORED_IMAGES[1],
    category: "Residential",
  },
  {
    id: 11,
    title: "Large open-plan pour for a new-build near Cullompton",
    location: "Cullompton, Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Over 200m² of flowing screed installed in a single day for a new executive home.",
    url: PROJECT_RESTORED_IMAGES[2],
    category: "Self-Build",
  },
  {
    id: 12,
    title: "UFH screed in a kitchen-diner extension, Taunton",
    location: "Taunton, Somerset",
    area: "Somerset",
    system: "Cemfloor Therm",
    description:
      "Fully encapsulated underfloor heating loops providing rapid heat response and lower energy bills.",
    url: PROJECT_RESTORED_IMAGES[3],
    category: "Residential",
  },
  {
    id: 13,
    title: "Underfloor heating screed pour with pipework",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Liquid screed pumped over underfloor heating pipes, wrapping them completely for maximum heat transfer and energy efficiency. Pipes laid out and secured before the pour for a seamless, air-gap-free finish.",
    url: UFH_SCREED_1,
    gallery: [UFH_SCREED_1, UFH_SCREED_2],
    category: "Residential",
  },
  {
    id: 14,
    title: "Liquid flow screed pour for a Devon home",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Pumped liquid flow screed laid to a laser-levelled finish across a new-build ground floor. The self-compacting screed fully encapsulates the underfloor heating loops for fast, even heat response and a crack-free surface ready for final flooring.",
    url: FLOW_SCREED_1,
    gallery: [FLOW_SCREED_1, FLOW_SCREED_2, FLOW_SCREED_3],
    category: "Residential",
  },
  {
    id: 15,
    title: "Before & after: liquid screed transformation in Devon",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "A before-and-after showing the subfloor preparation and finished pour. The first image shows the foundations with the membrane laid and the floor fully prepped, ready for the screed to be pumped in. The second image shows the liquid screed laid to a flawless, laser-levelled finish.",
    url: BEFORE_AFTER_1,
    gallery: [BEFORE_AFTER_1, BEFORE_AFTER_2],
    category: "Renovation",
  },
  {
    id: 16,
    title: "Before & after: underfloor heating screed pour in Devon",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "An underfloor heating screed job where the ground was fully prepped and ready to go. The first image shows the prepared subfloor with UFH pipework laid out, and the second shows the liquid screed pumped over the loops to a smooth, level finish.",
    url: BEFORE_AFTER_3,
    gallery: [BEFORE_AFTER_3, BEFORE_AFTER_4],
    category: "Renovation",
  },
  {
    id: 17,
    title: "Liquid screed pour for a residential extension in Devon",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "Pumped liquid screed laid to a smooth, level finish across a residential extension floor. The self-compacting screed fully encapsulates the underfloor heating loops for fast, even heat response.",
    url: PROJECT_A_IMG,
    category: "Residential",
  },
  {
    id: 18,
    title: "Fresh flow screed finish for a new-build in Devon",
    location: "Devon",
    area: "Devon",
    system: "Cemfloor Therm",
    description:
      "A flawless, laser-levelled liquid screed surface poured across a new-build ground floor. Crack-free and ready for final flooring with minimal drying time.",
    url: PROJECT_B_IMG,
    category: "Self-Build",
  },
];

const Projects: React.FC = () => {
  const [activeFilter, setActiveFilter] = React.useState<string>("All");
  const [lightboxProject, setLightboxProject] = useState<ProjectImage | null>(
    null,
  );
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const categories = [
    "All",
    "Residential",
    "Commercial",
    "Self-Build",
    "Renovation",
    "Garage",
  ];

  useEffect(() => {
    document.title = "Liquid Screed Projects Exeter & Devon | Past Work";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc)
      metaDesc.setAttribute(
        "content",
        "View recent liquid screed installations by Exeter Flow Screeds across Devon, Somerset & Dorset. Underfloor heating, garage, commercial & residential pours.",
      );
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle)
      ogTitle.setAttribute(
        "content",
        "Liquid Screed Projects Exeter & Devon | Past Work",
      );
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc)
      ogDesc.setAttribute(
        "content",
        "View recent liquid screed installations by Exeter Flow Screeds across Devon, Somerset & Dorset.",
      );
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl)
      ogUrl.setAttribute("content", "https://exeterflowscreed.co.uk/projects");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical)
      canonical.setAttribute("href", "https://exeterflowscreed.co.uk/projects");
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle)
      twTitle.setAttribute(
        "content",
        "Liquid Screed Projects Exeter & Devon | Past Work",
      );
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc)
      twDesc.setAttribute(
        "content",
        "View recent liquid screed installations by Exeter Flow Screeds across Devon, Somerset & Dorset.",
      );
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage)
      ogImage.setAttribute(
        "content",
        "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a85f76b5113a1715f109275.webp",
      );

    // Inject ImageGallery structured data
    const existingScript = document.getElementById("projects-jsonld");
    if (existingScript) existingScript.remove();
    const galleryJsonLd = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      name: "Exeter Flow Screeds Project Gallery",
      description:
        "Recent liquid screed installations across Devon, Somerset & Dorset.",
      image: PROJECT_IMAGES.map((p) => ({
        "@type": "ImageObject",
        contentUrl: p.url,
        name: p.title,
        description: p.description,
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "projects-jsonld";
    script.text = JSON.stringify(galleryJsonLd);
    document.head.appendChild(script);

    window.scrollTo(0, 0);
    return () => {
      const s = document.getElementById("projects-jsonld");
      if (s) s.remove();
    };
  }, []);

  const filteredProjects =
    activeFilter === "All"
      ? PROJECT_IMAGES
      : PROJECT_IMAGES.filter((p) => p.category === activeFilter);

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-stone-900 font-sans">
      <Navbar />

      <main className="pt-28 pb-20">
        <section className="bg-[#12121D] text-white py-16 sm:py-20 border-b border-stone-800 relative overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] bg-[#C9A84C]/10 px-3 py-1 rounded-full border border-[#C9A84C]/20">
                Our Work & Projects
              </span>
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mt-4">
                Real Floors.{" "}
                <span className="text-[#C9A84C]">Real Projects.</span>
              </h1>
              <p className="text-stone-300 text-base sm:text-lg mt-4 leading-relaxed">
                Explore our recent liquid screed installations across Exeter,
                Devon, and the South West.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="flex flex-wrap items-center gap-2 mb-10 pb-4 border-b border-stone-200">
            <span className="text-xs font-bold text-stone-500 uppercase tracking-wider mr-2">
              Filter:
            </span>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${
                  activeFilter === cat
                    ? "bg-[#12121D] text-[#C9A84C] shadow-md scale-105"
                    : "bg-stone-200/70 text-stone-700 hover:bg-stone-300"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg border border-stone-200 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5 group flex flex-col"
              >
                <div
                  className="relative aspect-[4/3] overflow-hidden bg-stone-900 cursor-pointer"
                  onClick={() => {
                    setLightboxProject(project);
                    setLightboxIndex(0);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open photo gallery for ${project.title}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setLightboxProject(project);
                      setLightboxIndex(0);
                    }
                  }}
                >
                  <LazyImg
                    src={project.url}
                    alt={project.title}
                    width={900}
                    height={675}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute top-3 left-3 bg-[#12121D]/90 backdrop-blur-sm text-[#C9A84C] text-xs font-bold px-3 py-1 rounded-full border border-[#C9A84C]/30">
                    {project.category}
                  </div>
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm text-stone-900 text-xs font-semibold px-2.5 py-1 rounded-md shadow">
                    {project.area}
                  </div>
                  {project.gallery && project.gallery.length > 1 && (
                    <>
                      <div className="absolute bottom-3 left-3 flex gap-1.5">
                        {project.gallery.map((gImg, gIdx) => (
                          <div
                            key={gIdx}
                            className={`w-2 h-2 rounded-full ${gIdx === 0 ? "bg-[#C9A84C]" : "bg-white/60"}`}
                          />
                        ))}
                      </div>
                      <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#12121D]/90 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full border border-white/20">
                        <Images className="w-3 h-3 text-[#C9A84C]" />
                        {project.gallery.length}
                      </div>
                    </>
                  )}
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-stone-500 text-xs font-medium mb-2">
                      <MapPin className="w-3.5 h-3.5 text-[#C9A84C]" />
                      <span>{project.location}</span>
                      <span>•</span>
                      <Layers className="w-3.5 h-3.5 text-[#C9A84C]" />
                      <span>{project.system}</span>
                    </div>
                    <h3 className="text-lg font-bold text-stone-900 group-hover:text-[#B38D30] transition-colors leading-snug">
                      {project.title}
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm mt-2 leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  <div className="pt-4 mt-4 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
                    <span className="flex items-center gap-1 font-semibold text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Cemfloor
                      Certified
                    </span>
                    <Link
                      to="/#contact"
                      onClick={() => trackQuote("projects_card")}
                      className="font-bold text-[#B38D30] hover:underline flex items-center gap-1"
                    >
                      Get Quote <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <div className="bg-[#12121D] text-white rounded-2xl p-8 sm:p-12 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 border border-stone-800">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold">
                Have a floor project in Devon or Somerset?
              </h3>
              <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-xl">
                Get a same-day fixed price quote with free site advice. Fully
                insured Cemfloor certified installers.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
              <a
                href="tel:07765712609"
                onClick={() => trackCall("07765 712609", "projects_cta")}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold px-6 py-3 rounded-xl text-sm transition-all shadow-md"
              >
                <Phone className="w-4 h-4" />
                <span>Call 07765 712609</span>
              </a>
              <Link
                to="/#contact"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-stone-700 hover:bg-stone-800 text-white font-bold px-6 py-3 rounded-xl text-sm transition-all"
              >
                <span>Get Fixed Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <ProjectLightbox
        project={lightboxProject}
        index={lightboxIndex}
        onClose={() => setLightboxProject(null)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
};

export default Projects;
